import { GridColDef } from '@mui/x-data-grid';
import { themeObject, validationsMessages } from 'lib';
import * as yup from 'yup';

export const stringWithNoEmptySpace = /^(?!\s*$)^(?!' +'$).*$/gm;

export const stringWithEmptySpace = /^'\s*'$/;

export type FilterMode = 'Basic' | 'Advanced';

// Basic filter helpers

export const styles = {
    formControl: { mx: 1, my: 0.5 },
    paper: {
        p: 2,
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        boxShadow: 'none',
    },
    errorText: { pl: 7 },
    limitError: { pl: 5 },
    depthError: { pl: 6 },
    chainSelectIcon: {
        height: '1.25rem',
        width: '1.25rem',
        backgroundColor: themeObject.palette.primary[100],
        color: themeObject.palette.grayScale[700],
        borderRadius: '16px',
        opacity: '.8',
        padding: '3px',
    },
    select: {
        height: '32px',
        ml: 1,
        '& .MuiSelect-icon': {
            color: themeObject.palette.primary.dark,
        },
    },
    chainSelect: {
        width: '195px',
        minHeight: '32px',
        ml: 1,
        '& .MuiSelect-icon': {
            color: themeObject.palette.primary.dark,
        },
        '& .MuiFormControl-root': {
            width: '100%',
            '& .MuiTextField-root': {
                '& .MuiInputBase-root': {
                    height: '32px',
                    pr: '50px',
                    '& .MuiInputBase-input': {
                        height: '100%',
                    },
                },
            },
        },
    },
    listItemsContainerStyles: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        width: '100%',
    },
    colorCodeCircleIconStyles: {
        width: '12px',
        height: '12px',
        flexShrink: 0,
        borderRadius: '50%',
    },
    ellipsifyListItemTextStyles: {
        textOverflow: 'ellipsis',
        wordBreak: 'break-all',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    containerStyles: {
        '& .MuiFormHelperText-root': {
            width: '140px',
            ml: '5px',
            height: 'auto',
        },
    },
    whereSelect: {
        mr: 1,
        width: '140px',
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: themeObject.palette.tertiary.red[300],
        },
    },
    input: {
        ml: 1,
        mr: 1,
        '& .MuiInputBase-root': {
            height: '32px',
            width: '140px',
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: themeObject.palette.tertiary.red[300],
        },
        '& .MuiFormHelperText-root': {
            width: '140px',
            ml: 0,
            height: 'auto',
        },
    },
    limit: {
        ml: 1,
        mr: 1,
        '& .MuiInputBase-root': {
            height: '32px',
            width: '68px',
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: themeObject.palette.tertiary.red[300],
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
    },
    buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        minWidth: '160px',
    },
    expandedButtonContainer: {
        flexDirection: 'row',
    },
    minimizedButtonContainer: {
        flexDirection: 'column',
    },
    applyButton: {
        margin: '4px 0',
    },
    advancedButton: {
        margin: '4px 0 4px 12px',
        padding: '4px 10px',
        whiteSpace: 'nowrap',
        minWidth: '160px',
    },
    errorContainerStyles: {
        mt: '2px',
    },
    fieldContainerStyles: {
        display: 'flex',
        alignItems: 'baseline',
    },
    limitAndDepthContainerStyles: {
        '& .MuiFormHelperText-root': {
            width: '68px',
            ml: '5px',
            height: 'auto',
        },
    },
    containerFlexStyles: {
        display: 'flex',
    },
    whereFieldLabelContainerStyles: {
        mt: '6px',
    },
    limitAndDepthFieldLabelContainerStyles: {
        mt: '7px',
    },
    selectFieldHeight: {
        maxHeight: '280px',
    },
    chainSelectFieldMenuItemStyles: {
        width: '176px',
    },
    popperStyles: {
        width: '258x',
        height: '92px',
    },
    tooltipText: {
        ...themeObject.typography.captions.default,
        color: themeObject.palette.background.paper,
    },
};

export interface IFormInputs {
    chain: string;
    limit: number;
    depth: number;
    attributeKey: string;
    attributeValue: string;
}

export interface ErrorObjType {
    error: boolean;
    message: string;
}

export const schema = yup.object({
    chain: yup.string().required(validationsMessages.required),
    limit: yup
        .number()
        .required('Required')
        .typeError('Required')
        .moreThan(0, 'Invalid'),
    depth: yup
        .number()
        .required('Required')
        .typeError('Required')
        .moreThan(0, 'Invalid'),
    attributeKey: yup.string().optional(),
    attributeValue: yup
        .string()
        .optional()
        .when('attributeKey', {
            is: (attributeKey: string) => attributeKey !== 'None',
            then: yup
                .string()
                .matches(stringWithNoEmptySpace, validationsMessages.required),
            otherwise: yup.string().optional(),
        }),
});

export const initialValues = {
    chain: '',
    limit: 1,
    depth: 1,
    attributeKey: '',
    attributeValue: '',
};

export const fieldGridSize = 9;
export const btnGridSize = 3;

// Advanced filter helpers

export const advancedFilterStyles = {
    paper: {
        p: 1,
        border: `1px solid ${themeObject.palette.grayScale[300]} `,
        borderRadius: '8px',
        boxShadow: 'none',
        height: '100%',
    },
    labelBox: { ml: 2 },
    codeMirror: {
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        borderRadius: '4px',
        padding: '12px 4px 12px 8px',
        height: 'calc(100% - 26px)',
    },
    applyBtn: { mt: 2, mb: 1 },
    simpleBtn: {
        mt: 2,
        mb: 1,
        ml: 1.5,
        padding: '4px 10px',
        backgroundColor: 'white',
        border: `2px solid ${themeObject.palette.primary[300]}`,
        '&:hover': {
            backgroundColor: 'white',
        },
    },
    codeMirrorHeight: '100%',
    codeMirrorWidth: {
        width: 'inherit',
        height: 'calc(100% - 94px)',
    },
    containerStyles: {
        height: '100%',
    },
    codeMirrorMinHeight: '80px',
};

export const advancedFilterDefaults = {
    query: '',
};
export interface AdvancedFormInputs {
    query: string;
}

export const AdvancedFilterSchema = yup.object({
    query: yup.string().required(),
});

export const historyColumns: GridColDef[] = [
    { field: 'query', headerName: 'TQL Query History', flex: 1 },
];

export const formatQuery = (query: string): string => {
    const keywords = ['WHERE', 'SORT', 'LIMIT', 'TRAVERSE'];
    const pattern = new RegExp(`\\b(${keywords.join('|')})\\b`, 'g');
    return query.replace(pattern, '\n$1');
};

export const isMultilineEllipsisActive = (
    element: HTMLDivElement,
    lineClamp: number
): boolean => {
    if (!element) {
        return false;
    }
    const computedStyle = window.getComputedStyle(element);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    if (isNaN(lineHeight)) {
        const temp = document.createElement('span');
        temp.innerText = 'M';
        temp.style.visibility = 'hidden';
        temp.style.font = computedStyle.font;
        document.body.appendChild(temp);
        const estimatedLineHeight = temp.offsetHeight;
        document.body.removeChild(temp);
        return element.offsetHeight > estimatedLineHeight * lineClamp;
    }
    return element.scrollHeight > lineHeight * lineClamp;
};
